// Upload of a product capability document on the shared resumable uploader: a keyboard-accessible drop target and button for one PDF up to the server's limit, then progress with Pause, Resume and Cancel, then the reading of the stored document followed live (page x of y) until it is ready; the same PDF again hands over the stored document, a document stored without being read says why in plain words (the AI service switched off, with a link to its settings for Admins, an assessment using it, a reading already running), the AI service switched off is said before an upload when the server reports it, and rejected files say why.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2Icon,
  FileUpIcon,
  InfoIcon,
  PauseIcon,
  PlayIcon,
  RotateCcwIcon,
  XIcon,
} from "lucide-react";
import { useEffect, useEffectEvent, useId, useRef, useState, type ReactNode } from "react";

import {
  capabilitiesGetCapabilityDocumentOptions,
  capabilitiesListCapabilityDocumentsQueryKey,
} from "@/api/__generated__/@tanstack/react-query.gen";
import type { CapabilityDocumentDetail } from "@/api/__generated__";
import type { ProblemCopy } from "@/api/problem";
import { useApiMeta } from "@/app/session";
import { ProblemState } from "@/components/common/ProblemState";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/cn";
import { formatBytes, formatDuration, plural } from "@/lib/format";
import { useUploader } from "@/lib/upload/useUpload";

import { documentTitle } from "./labels";
import { AiServiceOffAlert, AiServiceSettingsLink, useAiServiceOffCopy } from "./AiServiceOff";
import { isAiServiceOff, readingNotStartedCopy } from "./problems";
import { READING_POLL_MS, rememberReadingProblem } from "./queries";
import { ReadingStatus } from "./StatusChips";
import { capabilityUploadStore, capabilityUploadTransport, type StoredCapabilityDocument } from "./upload";

const DEFAULT_MAX_BYTES = 512 * 1024 * 1024;

export function isPdf(file: Pick<File, "name" | "type">): boolean {
  return file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
}

interface CapabilityUploaderProps {
  onStored?: ((stored: StoredCapabilityDocument) => void) | undefined;
  onDuplicate: (document: CapabilityDocumentDetail) => void;
  disabled?: boolean;
  compact?: boolean;
  footer?: (document: CapabilityDocumentDetail) => ReactNode;
}

function phaseText(phase: string, percent: number): string {
  switch (phase) {
    case "hashing":
      return "Checking the file…";
    case "checking":
      return "Looking for a stored copy…";
    case "verifying":
      return "Checking the upload…";
    case "paused":
      return `Paused at ${String(percent)} %. Resume continues from the parts already sent.`;
    default:
      return "";
  }
}

function ReadingAfterUpload({
  stored,
  footer,
}: {
  stored: StoredCapabilityDocument;
  footer: CapabilityUploaderProps["footer"];
}) {
  const detail = useQuery({
    ...capabilitiesGetCapabilityDocumentOptions({ path: { capability_doc_uid: stored.sha256 } }),
    initialData: stored.document,
    refetchInterval: (query) => (query.state.data?.status === "extracting" ? READING_POLL_MS : false),
  });
  const document = detail.data;
  const problem = stored.readingProblem;
  return (
    <div className="flex flex-col gap-3">
      <p className="m-0 flex items-center gap-2 text-ui text-verdict-ok-pill">
        <CheckCircle2Icon aria-hidden className="size-4" />
        Uploaded.
      </p>
      {problem !== null && document.status === "uploaded" ? (
        <NotStarted copy={readingNotStartedCopy(problem)} />
      ) : (
        <div className="flex flex-col gap-1.5">
          <ReadingStatus document={document} live />
          {document.status === "ready" ? (
            <p className="m-0 text-chip text-muted-foreground">
              {`${documentTitle(document)} · ${plural(document.capability_count, "entry", "entries")} read`}
            </p>
          ) : null}
        </div>
      )}
      {footer?.(document)}
    </div>
  );
}

function NotStarted({ copy }: { copy: ProblemCopy }) {
  const aiOff = isAiServiceOff(copy);
  const aiCopy = useAiServiceOffCopy();
  const shown = aiOff ? aiCopy : copy;
  return (
    <Alert variant="neutral">
      <InfoIcon aria-hidden />
      <AlertTitle>{shown.title}</AlertTitle>
      <AlertDescription>
        <p>The document is stored, but reading has not started.</p>
        <p>{shown.description}</p>
        {aiOff ? <AiServiceSettingsLink className="mt-2" /> : null}
      </AlertDescription>
    </Alert>
  );
}

export function CapabilityUploader({
  onStored,
  onDuplicate,
  disabled = false,
  compact = false,
  footer,
}: CapabilityUploaderProps) {
  const queryClient = useQueryClient();
  const inputId = useId();
  const input = useRef<HTMLInputElement>(null);
  const zone = useRef<HTMLDivElement>(null);
  const meta = useApiMeta();
  const maxBytes = meta?.upload_max_bytes ?? DEFAULT_MAX_BYTES;
  const controls = useUploader({
    transport: capabilityUploadTransport,
    records: capabilityUploadStore,
    profile: false,
  });
  const { state } = controls;
  const [dragging, setDragging] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const reported = useRef<string | null>(null);

  const settled = useEffectEvent((stored: StoredCapabilityDocument, duplicate: boolean) => {
    void queryClient.invalidateQueries({ queryKey: capabilitiesListCapabilityDocumentsQueryKey() });
    if (duplicate) {
      onDuplicate(stored.document);
      return;
    }
    rememberReadingProblem(
      queryClient,
      stored.sha256,
      stored.readingProblem === null ? null : readingNotStartedCopy(stored.readingProblem),
    );
    onStored?.(stored);
  });

  useEffect(() => {
    const stored = state.sourceFile;
    if ((state.phase === "stored" || state.phase === "duplicate") && stored !== null) {
      if (reported.current !== stored.sha256) {
        reported.current = stored.sha256;
        settled(stored, state.phase === "duplicate");
      }
    }
  }, [state.phase, state.sourceFile]);

  const choose = (file: File | undefined) => {
    setRefusal(null);
    if (file === undefined) {
      return;
    }
    if (!isPdf(file)) {
      setRefusal(`${file.name} is not a PDF. Choose a .pdf file.`);
      return;
    }
    if (file.size > maxBytes) {
      setRefusal(
        `${file.name} has ${formatBytes(file.size)}; uploads are limited to ${formatBytes(maxBytes)}.`,
      );
      return;
    }
    reported.current = null;
    controls.start(file);
  };

  const dropped = useEffectEvent((file: File | undefined) => {
    choose(file);
  });

  useEffect(() => {
    const node = zone.current;
    if (node === null || disabled) {
      return undefined;
    }
    const over = (event: DragEvent) => {
      event.preventDefault();
      setDragging(true);
    };
    const leave = () => {
      setDragging(false);
    };
    const drop = (event: DragEvent) => {
      event.preventDefault();
      setDragging(false);
      dropped(event.dataTransfer?.files[0]);
    };
    node.addEventListener("dragover", over);
    node.addEventListener("dragleave", leave);
    node.addEventListener("drop", drop);
    return () => {
      node.removeEventListener("dragover", over);
      node.removeEventListener("dragleave", leave);
      node.removeEventListener("drop", drop);
    };
  }, [disabled, state.phase]);

  const percent = state.sizeBytes > 0 ? Math.round((state.sentBytes / state.sizeBytes) * 100) : 0;

  if (state.phase === "duplicate") {
    return (
      <Alert variant="info">
        <InfoIcon aria-hidden />
        <AlertTitle>Already uploaded</AlertTitle>
        <AlertDescription>
          <p>This PDF was uploaded before, so the stored document opens instead.</p>
        </AlertDescription>
      </Alert>
    );
  }

  if (state.phase === "stored" && state.sourceFile !== null) {
    return (
      <div className="rounded-lg border bg-card p-4">
        <div className="mb-2 flex items-baseline justify-between gap-3">
          <span className="truncate text-ui font-semibold">{state.fileName}</span>
          <span className="shrink-0 text-chip text-muted-foreground">{formatBytes(state.sizeBytes)}</span>
        </div>
        <ReadingAfterUpload stored={state.sourceFile} footer={footer} />
      </div>
    );
  }

  if (state.phase !== "idle" && state.phase !== "rejected") {
    const moving = state.phase === "uploading" || state.phase === "paused";
    return (
      <div className="flex flex-col gap-3 rounded-lg border bg-card p-4" aria-live="polite">
        <div className="flex items-baseline justify-between gap-3">
          <span className="truncate text-ui font-semibold">{state.fileName}</span>
          <span className="shrink-0 text-chip text-muted-foreground">{formatBytes(state.sizeBytes)}</span>
        </div>
        {state.phase === "error" ? (
          <ProblemState problem={state.problem} compact />
        ) : (
          <>
            <Progress
              value={moving ? percent : null}
              striped={!moving}
              aria-label={`Upload of ${state.fileName ?? "the file"}`}
              aria-valuetext={`${String(percent)} %`}
            />
            <p className="m-0 text-chip text-muted-foreground">
              {state.phase === "uploading"
                ? `${String(percent)} % · ${formatBytes(state.sentBytes)} of ${formatBytes(state.sizeBytes)}${
                    state.secondsLeft === null ? "" : ` · about ${formatDuration(state.secondsLeft)} left`
                  }`
                : phaseText(state.phase, percent)}
            </p>
          </>
        )}
        <div className="flex gap-2">
          {state.phase === "uploading" ? (
            <Button size="sm" variant="outline" onClick={controls.pause}>
              <PauseIcon aria-hidden />
              Pause
            </Button>
          ) : null}
          {state.phase === "paused" || state.phase === "error" ? (
            <Button size="sm" onClick={controls.resume}>
              {state.phase === "error" ? <RotateCcwIcon aria-hidden /> : <PlayIcon aria-hidden />}
              Resume upload
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" onClick={controls.cancel}>
            <XIcon aria-hidden />
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2.5">
      {meta?.confidential_ai_enabled === false ? <AiServiceOffAlert /> : null}
      <div
        ref={zone}
        className={cn(
          "flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed bg-subtle text-center",
          compact ? "px-4 py-5" : "px-6 py-9",
          dragging && "border-b4 bg-selected",
          disabled && "opacity-60",
        )}
      >
        <FileUpIcon aria-hidden className="size-8 text-muted-foreground" />
        <div className="flex flex-col gap-1">
          <span className="text-ui font-semibold">Drop a document to compare here</span>
          <span className="text-chip text-muted-foreground">
            {`One PDF up to ${formatBytes(maxBytes)}. Its entries are read automatically; the same PDF uploaded again opens the stored document.`}
          </span>
        </div>
        <Button
          type="button"
          disabled={disabled}
          onClick={() => {
            input.current?.click();
          }}
        >
          Choose a PDF
        </Button>
        <input
          id={inputId}
          ref={input}
          type="file"
          accept="application/pdf,.pdf"
          className="sr-only"
          tabIndex={-1}
          aria-label="Document PDF"
          disabled={disabled}
          onChange={(event) => {
            choose(event.target.files?.[0]);
            event.target.value = "";
          }}
        />
      </div>
      {refusal !== null ? (
        <Alert variant="attn" role="alert">
          <AlertDescription>{refusal}</AlertDescription>
        </Alert>
      ) : null}
      {state.phase === "rejected" ? (
        <Alert variant="attn" role="alert">
          <AlertTitle>Rejected</AlertTitle>
          <AlertDescription>
            {state.problem instanceof Error ? state.problem.message : "The file was rejected."}
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
}
